import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import {
  catchError,
  filter,
  firstValueFrom,
  of,
  Subscription,
  take,
} from 'rxjs'
import { environment } from 'src/environments/environment'

import { AdobeLaunchService } from '../../server/adobe-launch/adobe-launch.service'
import { ProfileResponse } from '../models/profile-response.model'
import { AdobeAnalyticsService } from '../services/adobe-analytics/adobe-analytics.service'
import { AEMChunk } from '../services/AEM/aem-chunk.model'
import { CentralContentService } from '../services/content/central-content.service'
import { MainService } from '../services/main.service'

const NEGATIVE_ONE = -1
const specialChars = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/

@Component({
  selector: 'profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, OnDestroy {
  tickerOrFundId: string
  barChartSection: string
  routeSubscription: Subscription
  profileSubscription: Subscription
  isValidFund = true
  isInvalidFund = false
  isHiddenFundId = false
  innerWidth: number
  is529Fund: boolean
  adobeLaunchPage: any
  data: ProfileResponse
  isExternalFund: boolean
  isInternalFund: boolean
  nonVanguardFundsViewableFlag: boolean
  hiddenFunds: AEMChunk
  aemContentSub: Subscription
  bfpToggleContent: AEMChunk
  hasProxyVotingContent: boolean = false
  proxyVotingContent: string = ''

  bfpToggle: {
    classification: boolean
    diversification: boolean
    allocation: boolean
    characteristics: boolean
  } = {
    classification: false,
    diversification: false,
    allocation: false,
    characteristics: false,
  }

  // eslint-disable-next-line max-params
  constructor(
    private readonly route: ActivatedRoute,
    private readonly mainService: MainService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly centralContentService: CentralContentService,
    public adobeLaunchService: AdobeLaunchService,
  ) {}
  @HostListener('window:resize', ['$event']) onResize() {
    this.detectScreenSize()
  }
  ngAfterViewInit(): void {
    this.setNavBar()
  }

  detectScreenSize() {
    this.innerWidth = window.innerWidth
  }

  // eslint-disable-next-line max-statements
  async ngOnInit() {
    this.data = this.route.snapshot?.data?.fundDetails as ProfileResponse

    const tickerOrFundId = this.route.snapshot.paramMap.get('tickerOrFundId')
    this.tickerOrFundId = tickerOrFundId

    this.isInvalidFund = specialChars.test(this.tickerOrFundId)

    this.nonVanguardFundsViewableFlag = environment.nonVanguardFundsViewableFlag

    if (!this.isInvalidFund && this.data?.fundProfile) {
      // Step 1: Load AEM content first
      await this.loadAemContent()

      // Step 2: Set up fund data observables
      this.mainService.getFundData(
        this.tickerOrFundId,
        this.data.fundProfile?.isInternalFund,
        this.bfpToggle,
      )

      // Step 3: Set fund flags
      this.isExternalFund = this.data.fundProfile?.isExternalFund
      this.isInternalFund = this.data.fundProfile?.isInternalFund
      this.is529Fund = this.data.fundProfile?.is529

      // Step 4: Load proxy voting content asynchronously (non-blocking)
      // Wrapped to ensure errors don't stop page loading
      console.log(
        '🚀 [ngOnInit] Starting loadProxyVotingContent() in background',
      )
      this.loadProxyVotingContent().catch((error) => {
        console.error(
          '❌ [ngOnInit] Failed to load proxy voting content, continuing page load:',
          error,
        )
      })
      console.log(
        '⏭️ [ngOnInit] Continuing with page initialization (non-blocking)',
      )

      this.adobeLaunchPage = {
        pageTemplate: this.tickerOrFundId,
      }

      this.adobeLaunchService.initializePage(
        this.adobeLaunchPage,
        environment.adobeLaunchEnv,
      )

      this.detectScreenSize()
    }
  }

  async loadAemContent() {
    try {
      const content = await firstValueFrom(
        this.centralContentService.aemContent$,
      )
      this.parseAEMContent(content)
    } catch (error) {
      console.error('Error loading AEM content:', error)
    }
  }

  // eslint-disable-next-line max-statements, complexity
  async loadProxyVotingContent() {
    console.log(
      '🔵 [loadProxyVotingContent] Starting proxy voting content load',
    )

    try {
      // Ensure fundContent$ Observable exists before attempting to subscribe
      if (!this.mainService.fundContent$) {
        console.error(
          '❌ [loadProxyVotingContent] fundContent$ Observable not initialized',
        )
        this.hasProxyVotingContent = false
        this.proxyVotingContent = ''
        return
      }

      console.log(
        '✅ [loadProxyVotingContent] fundContent$ Observable exists, waiting for data...',
      )

      // Wait for fundContent$ to emit - runs in background, doesn't block page load
      const fundContent = await firstValueFrom(
        this.mainService.fundContent$.pipe(
          filter((content) => {
            const isValid = content != null
            console.log(
              `🔍 [loadProxyVotingContent] Filter check - Content is ${
                isValid ? 'valid' : 'null/undefined'
              }`,
            )
            return isValid
          }),
          take(1),
          catchError((error) => {
            console.error(
              '❌ [loadProxyVotingContent] Error in Observable stream:',
              error,
            )
            return of(null)
          }),
        ),
      )

      console.log(
        '📦 [loadProxyVotingContent] fundContent received:',
        fundContent,
      )

      if (fundContent && fundContent.fundVoting) {
        console.log('📝 [loadProxyVotingContent] fundVoting property exists')
        console.log(
          '📝 [loadProxyVotingContent] Raw fundVoting value:',
          fundContent.fundVoting,
        )
        console.log(
          '📝 [loadProxyVotingContent] fundVoting type:',
          typeof fundContent.fundVoting,
        )
        console.log(
          '📝 [loadProxyVotingContent] fundVoting length:',
          fundContent.fundVoting.length,
        )

        const trimmedValue = fundContent.fundVoting.trim()
        console.log('✂️ [loadProxyVotingContent] Trimmed value:', trimmedValue)
        console.log(
          '✂️ [loadProxyVotingContent] Trimmed length:',
          trimmedValue.length,
        )

        this.hasProxyVotingContent = trimmedValue.length > 0
        this.proxyVotingContent = fundContent.fundVoting

        console.log(
          '✅ [loadProxyVotingContent] hasProxyVotingContent set to:',
          this.hasProxyVotingContent,
        )
        console.log(
          '✅ [loadProxyVotingContent] proxyVotingContent set to:',
          this.proxyVotingContent,
        )

        // Trigger change detection to update UI when data arrives
        this.changeDetector.detectChanges()
        console.log('🔄 [loadProxyVotingContent] Change detection triggered')
      } else {
        console.warn('⚠️ [loadProxyVotingContent] No valid content received')
        console.log(
          '⚠️ [loadProxyVotingContent] fundContent exists:',
          !!fundContent,
        )
        console.log(
          '⚠️ [loadProxyVotingContent] fundVoting exists:',
          !!(fundContent && fundContent.fundVoting),
        )

        // No valid content received
        this.hasProxyVotingContent = false
        this.proxyVotingContent = ''
        console.log(
          '🚫 [loadProxyVotingContent] Set hasProxyVotingContent to false',
        )
      }

      console.log(
        '🏁 [loadProxyVotingContent] Proxy voting content load completed successfully',
      )
    } catch (error) {
      console.error('❌ [loadProxyVotingContent] Exception caught:', error)
      console.error('❌ [loadProxyVotingContent] Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
      })

      // Set defaults on error to prevent UI breaking
      this.hasProxyVotingContent = false
      this.proxyVotingContent = ''
      console.log('🚫 [loadProxyVotingContent] Set defaults due to error')
    }
  }

  parseAEMContent(content) {
    this.hiddenFunds = this.centralContentService.getContentByBody(
      'configuration_hidden_fund_ids',
      content,
    )

    this.bfpToggleContent = this.centralContentService.getContentByBody(
      'configuration_bfp_toggles',
      content,
    )

    this.bfpToggle = this.extractFlagsFromHtmlRegex(
      this.bfpToggleContent.contentValue,
    )

    this.validateFunds()
  }

  // eslint-disable-next-line complexity,max-statements
  extractFlagsFromHtmlRegex(htmlEncodedText: string | null | undefined): {
    classification: boolean
    diversification: boolean
    allocation: boolean
    characteristics: boolean
  } {
    try {
      if (!htmlEncodedText || htmlEncodedText.trim() === '') {
        return this.bfpToggle
      }

      // Step 1: Manually decode HTML entities
      const decoded = htmlEncodedText
        .replace(/&amp;lt;/g, '<')
        .replace(/&amp;gt;/g, '>')
        .replace(/&amp;quot;/g, '"')
        .replace(/&amp;amp;/g, '&')
        .replace(/&lt;br\s*\/?&gt;/gi, '')
        .replace(/&lt;\/?p&gt;/gi, '')
        .replace(/\n/g, '')

      // Step 2: Extract JSON-like string using regex
      const jsonMatch = decoded.match(/{[^}]+}/)
      if (!jsonMatch) {
        return this.bfpToggle
      }

      const jsonString = jsonMatch[0]

      // Step 3: Parse JSON
      const parsed = JSON.parse(jsonString)

      // Step 4: Return values
      return {
        classification: parsed.classification ?? false,
        diversification: parsed.diversification ?? false,
        allocation: parsed.allocation ?? false,
        characteristics: parsed.characteristics ?? false,
      }
    } catch (error) {
      console.error('Error extracting flags:', error)
      return this.bfpToggle
    }
  }

  validateFunds() {
    const hiddenFundsArray = this.hiddenFunds.contentValue?.split(',')
    this.isValidFund =
      hiddenFundsArray.indexOf(this.data.fundProfile?.fundId) === NEGATIVE_ONE
    this.isHiddenFundId =
      hiddenFundsArray.indexOf(this.data.fundProfile?.fundId) > NEGATIVE_ONE
  }

  setNavBar() {
    this.routeSubscription = this.route.fragment.subscribe((fragment) => {
      if (fragment) {
        this.barChartSection = fragment
        this.changeDetector.detectChanges()
      }
    })
  }
  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe()
    }
    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe()
    }
  }
}
